import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, 
  Check, 
  Zap, 
  AlertTriangle, 
  TrendingDown, 
  Activity,
  Droplets,
  Battery
} from "lucide-react";
import { toast } from "sonner";
import { subscribe } from "@/lib/api";
import { useProducts } from "@/lib/ProductsContext";
import { EducationalDisclaimer } from "@/components/LegalNotices";
import SeePricingLink from "@/components/SeePricingLink";
import { TestimonialCard } from "@/components/TestimonialCard";
import TrackerCTA from "@/components/TrackerCTA";
import SEO from "@/components/SEO";

const TESTIMONIALS = [
  { name: "Sarah M.", quote: "Finally understood why I was so exhausted. The protocol actually made sense.", tag: "Wegovy user", verified: true },
  { name: "James T.", quote: "Nausea was ruining my first month. This gave me a clear system to follow.", tag: "Ozempic user", verified: true },
  { name: "Rachel K.", quote: "I was losing muscle and had no idea. The guide changed how I approach meals.", tag: "Mounjaro user", verified: true },
  { name: "David L.", quote: "The daily check-in habit is what made everything click for me.", tag: "Zepbound user", verified: true },
  { name: "Maria S.", quote: "Bought the bundle on day 3 of starting. Wish I had found this sooner.", tag: "Wegovy user", verified: true },
];

// Trust strip — single positioning line
const TrustBadges = () => (
  <div
    className="flex items-center justify-center py-4 px-4 text-center text-sm md:text-base text-[#4B5548]"
    data-testid="trust-strip"
  >
    <p className="max-w-3xl leading-relaxed">
      <span className="font-semibold text-[#2D6A4F]">Built for Ozempic, Wegovy, Mounjaro and Zepbound users</span>
      <span className="mx-2 text-[#838F80]">—</span>
      For the side effects, plateaus, and everything nobody warned you about
    </p>
  </div>
);

const HomePage = () => {
  const { guides, bundle: BUNDLE, tools: TOOLS } = useProducts();
  const coreGuides = Object.values(guides).filter(g => g.category === "core");
  const supportingGuides = Object.values(guides).filter(g => g.category === "supporting");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const handleFreebieSignup = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }
    setLoading(true);
    try {
      await subscribe({ email, name, freebie_id: "101-protocols", source: "homepage", tags: ["101-protocols-subscriber"] });
      toast.success("Check your email for your free protocol guide!");
      setEmail("");
      setName("");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  const problems = [
    { icon: AlertTriangle, title: "Constant Nausea", description: "Struggling to eat without discomfort for hours after" },
    { icon: Battery, title: "Crushing Fatigue", description: "Low energy all the time, even with enough sleep" },
    { icon: Droplets, title: "Digestive Issues", description: "Constipation, bloating, and ongoing discomfort" },
    { icon: TrendingDown, title: "Weight Plateau", description: "Scale won't budge despite doing everything right" },
    { icon: Activity, title: "Muscle Loss", description: "Losing strength along with the weight" },
  ];

  return (
    <div className="noise-bg">
      <SEO title="The support system for everything that happens after the prescription | MetabolicLabOS" description="MetabolicLabOS is the support system for everything that happens after the prescription. Side effect protocols, AI coaching, daily tracking, and real answers for Ozempic, Wegovy, Mounjaro and Zepbound users." />
      {/* Hero Section */}
      <section className="gradient-hero section-padding relative overflow-hidden">
        <div className="container-main relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span
                className="inline-block px-4 py-2 bg-[#E9EDC9] text-[#2D6A4F] rounded-full text-sm font-medium mb-6"
                data-testid="hero-eyebrow"
              >
                The part nobody prepares you for.
              </span>
              <h1
                className="mb-3 text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight"
                style={{ color: "#1A2018", fontFamily: "Fraunces, Georgia, serif" }}
                data-testid="hero-headline"
              >
                Getting the prescription was the easy part.
              </h1>
              <p
                className="mb-8 text-2xl md:text-3xl lg:text-4xl leading-[1.15] tracking-tight font-medium"
                style={{ color: "#2D6A4F", fontFamily: "Fraunces, Georgia, serif" }}
                data-testid="hero-line-two"
              >
                MetabolicLabOS is the support system for everything that happens after.
              </p>
              <p className="text-lg md:text-xl text-[#4B5548] mb-8 leading-relaxed max-w-xl" data-testid="hero-subhead">
                Side effects. Plateaus. Fatigue. Food confusion. Isolation. We built the support layer for everything your prescriber didn't have time to explain.
              </p>
              <Link to="/start-here" className="btn-coral inline-flex items-center gap-2 text-lg px-8 py-4" data-testid="hero-cta-btn">
                Start Here <ArrowRight className="w-5 h-5" />
              </Link>
              <p className="text-sm text-[#838F80] mt-4">Free tools, free checklist, structured system. No guesswork.</p>
              <div className="mt-3">
                <SeePricingLink testid="homepage-hero-see-pricing" />
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }} className="relative hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-4 bg-[#E9EDC9] rounded-[40px] -rotate-3" />
                <img src="https://images.unsplash.com/photo-1674682629041-ebda18579326?auto=format&fit=crop&w=940&h=1175&q=80" alt="A couple walking outdoors together — staying active and hydrated as a daily ritual" className="relative rounded-3xl shadow-xl object-cover w-full aspect-[4/5]" fetchPriority="high" decoding="async" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <div className="bg-white border-b border-gray-100">
        <div className="container-main">
          <TrustBadges />
        </div>
      </div>

      {/* Problems Section */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="mb-4" data-testid="problems-headline">Sound familiar?</h2>
            <p className="text-lg text-[#4B5548] max-w-2xl mx-auto">
              Most GLP-1 users face these challenges. You're not alone—and there are strategies that can help.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {problems.map((problem, index) => (
              <motion.div
                key={problem.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-elevated p-6 text-center"
                data-testid={`problem-card-${index}`}
              >
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#E9EDC9] flex items-center justify-center">
                  <problem.icon className="w-6 h-6 text-[#2D6A4F]" />
                </div>
                <h3 className="text-lg font-semibold mb-2 text-[#1A2018]">{problem.title}</h3>
                <p className="text-sm text-[#4B5548]">{problem.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding bg-[#F2F0E9]">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="mb-4">How MetabolicLabOS Works</h2>
            <p className="text-lg text-[#4B5548]">A simple system for building better GLP-1 routines</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "1", title: "Log your day in 60 seconds", description: "Weight, symptoms, protein, water — by photo, voice, barcode, or quick-tap." },
              { step: "2", title: "Get coached by Kai", description: "Your AI coach already knows your last 7 days and flags what to fix next." },
              { step: "3", title: "Share progress with your doctor", description: "Generate a clinician PDF anytime — or auto-share weekly with Complete." },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-full gradient-bundle flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">{item.step}</span>
                </div>
                <h3 className="text-xl font-semibold mb-2 text-[#1A2018]">{item.title}</h3>
                <p className="text-[#4B5548]">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Free Tools Section */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="badge-popular mb-4 inline-block">Free</span>
            <h2 className="mb-4">Try Our Free Tools</h2>
            <p className="text-lg text-[#4B5548] max-w-2xl mx-auto">
              Get instant educational insights based on your specific experience
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {Object.values(TOOLS).map((tool, index) => (
              <motion.div
                key={tool.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link 
                  to={`/${tool.slug}`}
                  className="card-elevated p-6 block h-full hover:border-[#2D6A4F] border-2 border-transparent"
                  data-testid={`tool-card-${tool.id}`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-[#E9EDC9] flex items-center justify-center">
                      <Zap className="w-5 h-5 text-[#2D6A4F]" />
                    </div>
                    <span className="text-xs font-semibold uppercase tracking-wide text-[#2D6A4F]">Free Tool</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-[#1A2018]">{tool.title}</h3>
                  <p className="text-sm text-[#4B5548] mb-4">{tool.description}</p>
                  <span className="text-[#2D6A4F] font-medium text-sm flex items-center gap-1">
                    Try it now <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
            >
              <Link 
                to="/glp-pk-calculator"
                className="card-elevated p-6 block h-full hover:border-[#2D6A4F] border-2 border-transparent"
                data-testid="tool-card-pk-calculator"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[#E9EDC9] flex items-center justify-center">
                    <Zap className="w-5 h-5 text-[#2D6A4F]" />
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-wide text-[#2D6A4F]">Free Tool</span>
                </div>
                <h3 className="text-xl font-semibold mb-2 text-[#1A2018]">GLP-1 Level Calculator</h3>
                <p className="text-sm text-[#4B5548] mb-4">See your estimated medication concentration based on your dose and injection day.</p>
                <span className="text-[#2D6A4F] font-medium text-sm flex items-center gap-1">
                  Try it now <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
            >
              <Link
                to="/free/glp-1-readiness-checklist"
                className="card-elevated p-6 block h-full hover:border-[#2D6A4F] border-2 border-transparent"
                data-testid="tool-card-readiness-checklist"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[#E9EDC9] flex items-center justify-center">
                    <Zap className="w-5 h-5 text-[#2D6A4F]" />
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-wide text-[#2D6A4F]">Free Download</span>
                </div>
                <h3 className="text-xl font-semibold mb-2 text-[#1A2018]">GLP-1 Readiness Checklist</h3>
                <p className="text-sm text-[#4B5548] mb-4">Just got your prescription? The calm, structured 17-section guide for your first 30 days on Wegovy, Ozempic, Mounjaro or Zepbound.</p>
                <span className="text-[#2D6A4F] font-medium text-sm flex items-center gap-1">
                  Download free <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Bundle CTA Section */}
      <section className="section-padding gradient-bundle text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
        </div>
        
        <div className="container-main relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="badge-best-value mb-4 inline-block">Best Value</span>
              <h2 className="text-white mb-4">The Complete GLP Survival System</h2>
              <p className="text-lg text-white/80 mb-6">
                Everything you need to navigate GLP-1 medications with confidence. All 8 educational guides in one complete system.
              </p>

              {/* What's Included */}
              <div className="space-y-3 mb-8">
                {[...coreGuides, ...supportingGuides].slice(0, 6).map((guide) => (
                  <div key={guide.id} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-[#95D5B2] flex-shrink-0" />
                    <span className="text-white/90">{guide.title}</span>
                  </div>
                ))}
                <div className="flex items-center gap-3 text-white/70">
                  <Check className="w-5 h-5 text-[#95D5B2] flex-shrink-0" />
                  <span>+ 2 more comprehensive guides</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
                <div className="text-center mb-6">
                  <div className="flex items-center justify-center gap-3 mb-2">
                    <span className="text-2xl text-white/60 line-through">${BUNDLE?.original_value || 127}</span>
                    <span className="text-5xl font-bold text-white">${BUNDLE?.price || 49}</span>
                  </div>
                  <p className="text-[#95D5B2] font-medium">Save over 60% with the bundle</p>
                </div>

                <div className="bg-white/10 rounded-xl p-4 mb-6">
                  <p className="text-sm text-white/80 text-center">
                    <span className="font-semibold text-white">Most users choose the full system</span><br />
                    Individual guides are $15-$19 each
                  </p>
                </div>

                <Link
                  to="/glp-survival-system"
                  className="btn-coral w-full justify-center text-lg"
                  data-testid="bundle-cta-btn"
                >
                  Get the Complete System
                  <ArrowRight className="w-5 h-5" />
                </Link>

                <p className="text-xs text-white/60 text-center mt-4">
                  Instant digital access. Educational resources only — not medical advice.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Individual Guides Preview */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="mb-4">Or Get Individual Guides</h2>
            <p className="text-lg text-[#4B5548] max-w-2xl mx-auto">
              Choose the educational guide that addresses your biggest challenge
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.values(guides).slice(0, 4).map((guide, index) => (
              <motion.div
                key={guide.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link 
                  to={`/guides/${guide.slug}`}
                  className="card-elevated p-6 block h-full"
                  data-testid={`guide-card-${guide.id}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className={`text-xs font-semibold uppercase tracking-wide ${
                      guide.category === "core" ? "text-[#E07A5F]" : "text-[#2D6A4F]"
                    }`}>
                      {guide.category === "core" ? "Core Guide" : "Supporting Guide"}
                    </span>
                    <span className="font-bold text-[#1A2018]">${guide.price}</span>
                  </div>
                  <h3 className="text-lg font-semibold mb-2 text-[#1A2018] leading-tight">{guide.title}</h3>
                  <p className="text-sm text-[#4B5548] line-clamp-2">{guide.description}</p>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link 
              to="/glp-survival-system" 
              className="link-underline"
              data-testid="view-all-guides-link"
            >
              View all 8 guides →
            </Link>
          </div>
        </div>
      </section>

      {/* Tracker + Community hero CTA */}
      <TrackerCTA variant="hero" source="homepage" />

      {/* Final CTA */}
      <section className="section-padding bg-[#F2F0E9]">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="mb-4">Ready to take charge of your GLP-1 journey?</h2>
            <p className="text-lg text-[#4B5548] mb-8">
              Join GLP-1 users who've built better routines and found strategies that work for them.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/glp-survival-system" className="btn-primary" data-testid="final-cta-bundle">
                Get the Full System - $49
              </Link>
              <Link to="/start-here" className="btn-secondary" data-testid="final-cta-freebie">
                Start Here (Free)
              </Link>
            </div>
            <EducationalDisclaimer className="mt-6" />
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
